import { Suspense } from 'react';

import { createRoot } from 'react-dom/client';

import { AppProviders } from '@bootstrap/providers/index.tsx';

import '@assets/styles/panel.css';
import '@assets/styles/token-bridge.css';
import '@assets/styles/styles.scss';

import APLoading from '@shared/components/molecules/loading';
import { env } from '@shared/utils/env';
import { API18n } from '@shared/utils/i18n/i18n';
import APUtilResources from '@shared/utils/resources/resources.utils';
import { APUtilIndexedDB, INDEXDB_TABLE_NAMES } from '@shared/utils/storage/indexdb.util';
import { installNetworkInterceptor } from '@shared/utils/network-interceptor';

import { installPrEnvHeaders } from '@bootstrap/pr-env/install-pr-env-headers';

// Install fetch interceptor early so all subsequent requests are captured
installNetworkInterceptor();

// Ephemeral PR-env routing headers (TST only) — before React, so the session request that fires
// during auth initialisation carries them too. No-op outside TST. INF-856.
installPrEnvHeaders();

// Initialize i18n
API18n.init({
  storageKey: '__AP_LANGUAGE__',
  bucketUrl: `${env.APP_PUBLIC_FOLDER}/locales/`,
  // No periodic reload: locale URLs are content-hashed and immutable, so new translations arrive
  // with a new build, which the version check announces (shared/components/organisms/app-version).
  flagUrl: `/img/flags/`,
  query: 'language',
  supportedLanguages: 'all',
  defaultLanguage: 'en',
});

// Configure resource utility
APUtilResources.setSettings({
  base: env.APP_RESOURCES_BASE_URL,
  paths: {
    images: 'img',
    icons: 'img/mingcute_icons',
    countryFlags: 'img/flags',
    currencyFlags: 'img/currencies',
  },
});

APUtilIndexedDB.init({
  dbName: 'astropay',
  tables: [
    {
      tableName: INDEXDB_TABLE_NAMES.service_cache,
      keyPath: 'preloadId',
      dropOnVersionChange: true,
    },
    {
      tableName: INDEXDB_TABLE_NAMES.icons_mcute,
      keyPath: 'icon',
      dropOnVersionChange: false,
    },
  ],
});

// ── Version-based cache bust ─────────────────────────────────────────────────
// Polls the server every 5 minutes fetching index.html and comparing the
// version meta tag. If a new deploy is detected the page is reloaded so users
// working on stale tabs always get the latest version automatically.
const startVersionPolling = () => {
  const currentVersion = document.querySelector('meta[name="version"]')?.getAttribute('content');
  if (!currentVersion || currentVersion.startsWith('%')) return;

  const check = async () => {
    try {
      const res = await fetch(import.meta.env.BASE_URL || '/', { cache: 'no-store' });
      if (!res.ok) return; // Server error during deploy — skip this check
      const html = await res.text();
      const match = html.match(/<meta name="version" content="([^"]+)"/);
      const serverVersion = match?.[1];
      if (serverVersion && serverVersion !== currentVersion) {
        window.location.reload();
      }
    } catch {
      // Network error — fail silently, try again next interval
    }
  };

  const intervalId = setInterval(check, 5 * 60 * 1000);
  window.addEventListener('beforeunload', () => clearInterval(intervalId));
};

startVersionPolling();
// ─────────────────────────────────────────────────────────────────────────────

createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<APLoading />}>
    <AppProviders />
  </Suspense>
);
